import { useEffect, useRef, useMemo, useState } from "react";
import api from "../api/client";
import { usePublishScreenData } from "../utils/aiScreenContext";
import { useSort, sortRows, SortableTh } from "../components/SortableTh";
import { formatDateTime } from "../utils/datetime";
import { useScrollIntoViewOn } from "../components/useScrollIntoViewOn";

// Scheduled / recurring reboots that auto-enter maintenance mode around the
// reboot. Backed by /api/v1/reboot-schedules.

type Schedule = {
  id: number;
  name: string;
  enabled: boolean;
  scope: string;
  customer_id?: number | null;
  customer_name?: string | null;
  device_id?: number | null;
  device_hostname?: string | null;
  network_device_id?: number | null;
  network_device_name?: string | null;
  schedule_type: string;
  run_at?: string | null;
  days_of_week: number[];
  start_time?: string | null;
  timezone?: string | null;
  maintenance_minutes: number;
  last_run_at?: string | null;
  /** Who owns the TARGET. A device-scoped schedule has no customer_id of its
   *  own, and retargeting it means picking a customer first — so the form
   *  needs the device's owner or it opens with an empty customer box and the
   *  save is refused for a missing device_id. */
  target_customer_id?: number | null;
  target_customer_name?: string | null;
};

type Customer = { id: number; name: string };

const DAYS = [
  { v: 0, l: "Mon" },
  { v: 1, l: "Tue" },
  { v: 2, l: "Wed" },
  { v: 3, l: "Thu" },
  { v: 4, l: "Fri" },
  { v: 5, l: "Sat" },
  { v: 6, l: "Sun" },
];

const TZS = [
  "America/New_York",
  "America/Chicago",
  "America/Denver",
  "America/Los_Angeles",
  "America/Phoenix",
  "UTC",
];

type DeviceLite = { id: number; hostname: string; status?: string };
type NetDeviceLite = { id: number; name: string; kind: string };

const blankForm = {
  name: "Scheduled reboot",
  scope: "global",
  customer_id: "" as number | "",
  device_ids: [] as number[],
  network_device_id: "" as number | "",
  schedule_type: "recurring",
  run_at: "",
  days_of_week: [] as number[],
  start_time: "03:00",
  timezone: "America/New_York",
  maintenance_minutes: 30,
};

function summarize(s: Schedule): string {
  if (s.schedule_type === "recurring") {
    const days = (s.days_of_week || []).map((d) => DAYS.find((x) => x.v === d)?.l).join(", ");
    return `${days || "no days"} at ${s.start_time} ${s.timezone || ""}`.trim();
  }
  return s.run_at ? formatDateTime(s.run_at) : "—";
}

export default function RebootSchedules() {
  const [rows, setRows] = useState<Schedule[]>([]);
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [devices, setDevices] = useState<DeviceLite[]>([]);
  const [netDevices, setNetDevices] = useState<NetDeviceLite[]>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ ...blankForm });
  const [editing, setEditing] = useState<Schedule | null>(null);
  // The form renders ABOVE the table. With 45 schedules, clicking Edit on a
  // row below the fold opened it off-screen and looked like nothing
  // happened - which is exactly how it was reported.
  const formRef = useRef<HTMLDivElement>(null);
  const [saving, setSaving] = useState(false);
  const schedSort = useSort("name");

  // Publish the visible rows so the global AI Assist can answer
  // "what's on my screen". Projected explicitly - credential and
  // machine-output fields must not reach a prompt.
  usePublishScreenData(
    "Reboot schedules",
    useMemo(
      () =>
        rows.map((r: any) => ({
          name: r.name,
          customer: r.customer_name ?? null,
          enabled: r.enabled,
          cadence: r.cadence ?? null,
          day_of_week: r.day_of_week ?? null,
          hour: r.hour ?? null,
          timezone: r.timezone ?? null,
          device_count: Array.isArray(r.device_ids) ? r.device_ids.length : null,
        })),
      [rows],
    ),
    undefined,
  );

  const load = () => {
    setLoading(true);
    setErr("");
    api
      .get("/api/v1/reboot-schedules")
      .then((r) => setRows(r.data || []))
      .catch((e) => setErr(e.response?.data?.detail || e.message))
      .finally(() => setLoading(false));
  };
  useEffect(() => {
    load();
    api
      .get("/api/v1/customers")
      .then((r) => setCustomers(r.data || []))
      .catch(() => {});
  }, []);

  // Device multi-select for scope="device": load the chosen customer's devices.
  // We pass customer_id explicitly so the list is scoped to the form's customer
  // (independent of the global switcher).
  useEffect(() => {
    if (form.scope === "device" && form.customer_id) {
      api
        .get("/api/v1/devices", { params: { customer_id: form.customer_id } })
        .then((r) => setDevices(r.data || []))
        .catch(() => setDevices([]));
    } else {
      setDevices([]);
    }
  }, [form.scope, form.customer_id]);

  // Network-device picker for scope="network_device": only reboot-capable kinds
  // (switch/router/firewall) for the chosen customer.
  useEffect(() => {
    if (form.scope === "network_device" && form.customer_id) {
      api
        .get("/api/v1/network-devices", { params: { customer_id: form.customer_id } })
        .then((r) =>
          setNetDevices(
            (r.data || []).filter((d: NetDeviceLite) =>
              ["switch", "router", "firewall"].includes(d.kind),
            ),
          ),
        )
        .catch(() => setNetDevices([]));
    } else {
      setNetDevices([]);
    }
  }, [form.scope, form.customer_id]);

  const toggleDevice = (id: number) =>
    setForm((f) => ({
      ...f,
      device_ids: f.device_ids.includes(id)
        ? f.device_ids.filter((x) => x !== id)
        : [...f.device_ids, id],
    }));

  const toggleDay = (d: number) =>
    setForm((f) => ({
      ...f,
      days_of_week: f.days_of_week.includes(d)
        ? f.days_of_week.filter((x) => x !== d)
        : [...f.days_of_week, d].sort((a, b) => a - b),
    }));

  const save = async () => {
    setSaving(true);
    setErr("");
    try {
      if (!editing && form.scope === "device" && form.device_ids.length === 0) {
        setErr("Select at least one device");
        setSaving(false);
        return;
      }
      if (!editing && form.scope === "network_device" && !form.network_device_id) {
        setErr("Select a network device");
        setSaving(false);
        return;
      }
      const base: Record<string, unknown> = {
        name: form.name,
        schedule_type: form.schedule_type,
        maintenance_minutes: Number(form.maintenance_minutes) || 0,
      };
      if (form.schedule_type === "recurring") {
        base.days_of_week = form.days_of_week;
        base.start_time = form.start_time;
        base.timezone = form.timezone;
      } else {
        base.run_at = form.run_at ? new Date(form.run_at).toISOString() : null;
      }
      if (editing) {
        // Send the target from the FORM, not from the row we opened. Edit
        // used to force `scope: editing.scope` and the original id back on,
        // so retargeting was impossible even if the fields had been
        // editable.
        const body: Record<string, unknown> = { ...base, scope: form.scope };
        if (form.scope === "customer") body.customer_id = form.customer_id || null;
        if (form.scope === "device") body.device_id = form.device_ids[0] ?? null;
        if (form.scope === "network_device")
          body.network_device_id = form.network_device_id || null;
        await api.put(`/api/v1/reboot-schedules/${editing.id}`, body);
      } else if (form.scope === "device") {
        // One device-scoped schedule per selected device.
        for (const id of form.device_ids) {
          await api.post("/api/v1/reboot-schedules", { ...base, scope: "device", device_id: id });
        }
      } else if (form.scope === "network_device") {
        await api.post("/api/v1/reboot-schedules", {
          ...base,
          scope: "network_device",
          network_device_id: form.network_device_id || null,
        });
      } else {
        const body: Record<string, unknown> = { ...base, scope: form.scope };
        if (form.scope === "customer") body.customer_id = form.customer_id || null;
        await api.post("/api/v1/reboot-schedules", body);
      }
      setShowForm(false);
      setEditing(null);
      setForm({ ...blankForm });
      load();
    } catch (e: any) {
      setErr(e.response?.data?.detail || e.message);
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id: number) => {
    if (!window.confirm("Delete this reboot schedule?")) return;
    try {
      await api.delete(`/api/v1/reboot-schedules/${id}`);
      load();
    } catch (e: any) {
      setErr(e.response?.data?.detail || e.message);
    }
  };

  const custName = (id?: number | null) =>
    id ? customers.find((c) => c.id === id)?.name || `#${id}` : "—";

  const targetLabel = (s: Schedule) =>
    s.scope === "global"
      ? "All devices"
      : s.scope === "customer"
        ? s.customer_name || custName(s.customer_id)
        : s.scope === "device"
          ? s.device_hostname || `Device #${s.device_id}`
          : s.scope === "network_device"
            ? `🌐 ${s.network_device_name || `Network device #${s.network_device_id}`}`
            : s.scope;

  // ISO (UTC) → the value a <input type=datetime-local> expects (local wall clock).
  const toLocalInput = (iso?: string | null) => {
    if (!iso) return "";
    const d = new Date(iso);
    return new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
  };

  const startEdit = (s: Schedule) => {
    setEditing(s);
    setForm({
      ...blankForm,
      name: s.name,
      scope: s.scope,
      // target_customer_id first: for a device-scoped schedule the row's own
      // customer_id is NULL, and the customer picker drives the device list.
      customer_id: s.customer_id ?? s.target_customer_id ?? "",
      // Seeded so the pickers below open on the schedule's CURRENT target
      // rather than empty, which would read as "this is now unassigned".
      device_ids: s.device_id ? [s.device_id] : [],
      network_device_id: s.network_device_id ?? "",
      schedule_type: s.schedule_type,
      run_at: toLocalInput(s.run_at),
      days_of_week: s.days_of_week || [],
      start_time: s.start_time || "03:00",
      timezone: s.timezone || "America/New_York",
      maintenance_minutes: s.maintenance_minutes ?? 30,
    });
    setShowForm(true);
  };

  // Scroll the editor to the operator, not the operator to the editor.
  useScrollIntoViewOn(formRef, editing ? `edit-${editing.id}` : null);

  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900 dark:text-gray-100">
            Reboot Schedules
          </h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            Schedule one-off or recurring reboots. Each automatically puts the device into
            maintenance mode around the reboot so it doesn't trip an offline alert.
          </p>
        </div>
        <button
          onClick={() => {
            setForm({ ...blankForm });
            setEditing(null);
            setShowForm((v) => !v);
          }}
          className="px-3 py-1.5 rounded-lg text-sm bg-brand text-white shrink-0"
        >
          {showForm ? "Cancel" : "New schedule"}
        </button>
      </div>

      {err && (
        <div className="bg-red-50 dark:bg-red-900/40 border border-red-200 dark:border-red-800 text-red-800 dark:text-red-300 px-4 py-2 rounded-lg text-sm">
          {err}
        </div>
      )}

      {showForm && (
        <div
          ref={formRef}
          className="bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 shadow-sm p-4 space-y-3"
        >
          {editing && (
            <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
              Editing <span className="text-brand dark:text-brand-light">{editing.name}</span>
            </div>
          )}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <label className="text-sm">
              <span className="block text-gray-600 dark:text-gray-300 mb-1">Name</span>
              <input
                className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-2 py-1.5 dark:bg-gray-900"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
              />
            </label>
            <label className="text-sm">
              <span className="block text-gray-600 dark:text-gray-300 mb-1">Applies to</span>
              {/* Retargeting used to be refused here with "delete & recreate",
                  and there was nothing behind the lock: the PUT handler's
                  _apply() already sets scope and clears/sets the scope id.
                  The reported workaround - going to All Devices and editing
                  from the device side - was working around a limitation the
                  API never had. */}
              <select
                className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-2 py-1.5 dark:bg-gray-900"
                value={form.scope}
                onChange={(e) =>
                  setForm({
                    ...form,
                    scope: e.target.value,
                    // Changing WHAT it applies to invalidates the old target.
                    device_ids: [],
                    network_device_id: "",
                  })
                }
              >
                <option value="global">All devices</option>
                <option value="customer">A customer</option>
                <option value="device">
                  {editing ? "A specific device" : "Specific device(s)"}
                </option>
                <option value="network_device">A network device (switch/router/firewall)</option>
              </select>
            </label>
            {(form.scope === "customer" ||
              form.scope === "device" ||
              form.scope === "network_device") && (
              <label className="text-sm">
                <span className="block text-gray-600 dark:text-gray-300 mb-1">Customer</span>
                <select
                  className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-2 py-1.5 dark:bg-gray-900"
                  value={form.customer_id}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      customer_id: e.target.value ? Number(e.target.value) : "",
                      device_ids: [],
                      network_device_id: "",
                    })
                  }
                >
                  <option value="">Select…</option>
                  {customers.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name}
                    </option>
                  ))}
                </select>
              </label>
            )}
            <label className="text-sm">
              <span className="block text-gray-600 dark:text-gray-300 mb-1">Type</span>
              <select
                className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-2 py-1.5 dark:bg-gray-900"
                value={form.schedule_type}
                onChange={(e) => setForm({ ...form, schedule_type: e.target.value })}
              >
                <option value="recurring">Recurring (weekly)</option>
                <option value="one_off">One-off</option>
              </select>
            </label>
            <label className="text-sm">
              <span className="block text-gray-600 dark:text-gray-300 mb-1">
                Maintenance window (minutes)
              </span>
              <input
                type="number"
                min={0}
                className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-2 py-1.5 dark:bg-gray-900"
                value={form.maintenance_minutes}
                onChange={(e) => setForm({ ...form, maintenance_minutes: Number(e.target.value) })}
              />
            </label>
          </div>

          {form.scope === "network_device" && (
            <div>
              <label className="text-sm">
                <span className="block text-gray-600 dark:text-gray-300 mb-1">Network device</span>
                <select
                  className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-2 py-1.5 dark:bg-gray-900"
                  value={form.network_device_id}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      network_device_id: e.target.value ? Number(e.target.value) : "",
                    })
                  }
                >
                  <option value="">
                    {form.customer_id ? "Select a device…" : "Select a customer first"}
                  </option>
                  {netDevices.map((d) => (
                    <option key={d.id} value={d.id}>
                      {d.name} ({d.kind})
                    </option>
                  ))}
                </select>
              </label>
              <p className="text-xs text-gray-400 mt-1">
                Reboots via the collector SSH shell (Cisco reload). Only switches, routers, and
                firewalls are listed.
              </p>
            </div>
          )}

          {/* A schedule points at ONE device, but creating device-scoped
              schedules fans out to one per box - so create offers a
              multi-select and edit offers a single choice. This asymmetry is
              why retargeting was locked rather than built. */}
          {editing && form.scope === "device" && (
            <label className="text-sm block">
              <span className="block text-gray-600 dark:text-gray-300 mb-1">Device</span>
              <select
                className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-2 py-1.5 dark:bg-gray-900"
                value={form.device_ids[0] ?? ""}
                onChange={(e) =>
                  setForm({
                    ...form,
                    device_ids: e.target.value ? [Number(e.target.value)] : [],
                  })
                }
              >
                <option value="">
                  {form.customer_id ? "Select a device…" : "Select a customer first"}
                </option>
                {devices.map((d) => (
                  <option key={d.id} value={d.id}>
                    {d.hostname}
                    {d.status ? ` (${d.status})` : ""}
                  </option>
                ))}
              </select>
            </label>
          )}

          {!editing && form.scope === "device" && (
            <div>
              <span className="block text-sm text-gray-600 dark:text-gray-300 mb-1">
                Devices ({form.device_ids.length} selected)
              </span>
              <div className="max-h-48 overflow-y-auto border border-gray-200 dark:border-gray-700 rounded-lg p-2 space-y-1">
                {!form.customer_id ? (
                  <p className="text-xs text-gray-400">
                    Select a customer above to list its devices.
                  </p>
                ) : devices.length === 0 ? (
                  <p className="text-xs text-gray-400">No devices found for this customer.</p>
                ) : (
                  devices.map((d) => (
                    <label
                      key={d.id}
                      className="flex items-center justify-between gap-2 text-sm py-0.5"
                    >
                      <span className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          checked={form.device_ids.includes(d.id)}
                          onChange={() => toggleDevice(d.id)}
                        />
                        <span>{d.hostname}</span>
                      </span>
                      <span
                        className={`text-[10px] px-1.5 py-0.5 rounded-full ${
                          d.status === "online"
                            ? "bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-300"
                            : "bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-300"
                        }`}
                      >
                        {d.status || "unknown"}
                      </span>
                    </label>
                  ))
                )}
              </div>
              <p className="text-[11px] text-gray-400 mt-1">
                The reboot fires only for devices that are online at the scheduled time, and
                automatically enters maintenance mode around the reboot.
              </p>
            </div>
          )}

          {form.schedule_type === "recurring" ? (
            <div className="space-y-3">
              <div>
                <span className="block text-sm text-gray-600 dark:text-gray-300 mb-1">Days</span>
                <div className="flex flex-wrap gap-1.5">
                  {DAYS.map((d) => (
                    <button
                      key={d.v}
                      type="button"
                      onClick={() => toggleDay(d.v)}
                      className={`px-2.5 py-1 rounded-lg text-sm border ${
                        form.days_of_week.includes(d.v)
                          ? "bg-brand text-white border-brand"
                          : "border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-300"
                      }`}
                    >
                      {d.l}
                    </button>
                  ))}
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <label className="text-sm">
                  <span className="block text-gray-600 dark:text-gray-300 mb-1">Time</span>
                  <input
                    type="time"
                    className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-2 py-1.5 dark:bg-gray-900"
                    value={form.start_time}
                    onChange={(e) => setForm({ ...form, start_time: e.target.value })}
                  />
                </label>
                <label className="text-sm">
                  <span className="block text-gray-600 dark:text-gray-300 mb-1">Timezone</span>
                  <select
                    className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-2 py-1.5 dark:bg-gray-900"
                    value={form.timezone}
                    onChange={(e) => setForm({ ...form, timezone: e.target.value })}
                  >
                    {TZS.map((t) => (
                      <option key={t} value={t}>
                        {t}
                      </option>
                    ))}
                  </select>
                </label>
              </div>
            </div>
          ) : (
            <label className="text-sm block">
              <span className="block text-gray-600 dark:text-gray-300 mb-1">Reboot at</span>
              <input
                type="datetime-local"
                className="w-full md:w-1/2 border border-gray-300 dark:border-gray-600 rounded-lg px-2 py-1.5 dark:bg-gray-900"
                value={form.run_at}
                onChange={(e) => setForm({ ...form, run_at: e.target.value })}
              />
            </label>
          )}

          <div className="flex justify-end">
            <button
              onClick={save}
              disabled={saving}
              className="px-4 py-1.5 rounded-lg text-sm bg-brand text-white disabled:opacity-50"
            >
              {saving ? "Saving…" : editing ? "Update schedule" : "Create schedule"}
            </button>
          </div>
        </div>
      )}

      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 shadow-sm overflow-x-auto">
        {loading ? (
          <div className="p-6 text-sm text-gray-400">Loading…</div>
        ) : rows.length === 0 ? (
          <div className="p-6 text-sm text-gray-500 dark:text-gray-400">
            No reboot schedules yet.
          </div>
        ) : (
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50 dark:bg-gray-900 text-xs uppercase text-gray-500 dark:text-gray-400">
              <tr>
                <SortableTh label="Name" sortKey="name" sort={schedSort.sort} onSort={schedSort.toggle} />
                <th className="px-3 py-2 text-left">Applies to</th>
                <th className="px-3 py-2 text-left">When</th>
                <SortableTh label="Maint." sortKey="maintenance_minutes" sort={schedSort.sort} onSort={schedSort.toggle} />
                <SortableTh label="Last run" sortKey="last_run_at" sort={schedSort.sort} onSort={schedSort.toggle} />
                <th className="px-3 py-2"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
              {sortRows(rows, schedSort.sort, (s, key) => {
                switch (key) {
                  case "maintenance_minutes":
                    return s.maintenance_minutes;
                  case "last_run_at":
                    return s.last_run_at ? Date.parse(s.last_run_at) : 0;
                  default:
                    return (s.name || "").toLowerCase();
                }
              }).map((s) => (
                <tr key={s.id}>
                  <td className="px-3 py-2 font-medium">{s.name}</td>
                  <td className="px-3 py-2">{targetLabel(s)}</td>
                  <td className="px-3 py-2 text-gray-600 dark:text-gray-300">{summarize(s)}</td>
                  <td className="px-3 py-2 text-gray-500 dark:text-gray-400">{s.maintenance_minutes}m</td>
                  <td className="px-3 py-2 text-xs text-gray-500 dark:text-gray-400">
                    {s.last_run_at ? formatDateTime(s.last_run_at) : "—"}
                  </td>
                  <td className="px-3 py-2 text-right whitespace-nowrap">
                    <button
                      onClick={() => startEdit(s)}
                      className="text-xs px-2 py-1 border border-gray-300 dark:border-gray-600 rounded hover:bg-gray-50 dark:hover:bg-gray-700 mr-1.5"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => remove(s.id)}
                      className="text-xs px-2 py-1 border border-gray-300 dark:border-gray-600 rounded hover:bg-gray-50 dark:hover:bg-gray-700"
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
